/**
 * AdminReviewDetailPage - View and manage AI review run
 *
 * Drives:
 * - GET /api/admin/reviews/:review_id
 * - GET /api/admin/reviews/:review_id/items
 * - POST /api/admin/reviews/:review_id/approve
 * - POST /api/admin/reviews/:review_id/apply
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  RefreshCw, AlertTriangle, ChevronLeft, CheckCircle2,
  Clock, Check, ChevronDown, ChevronUp, ExternalLink
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

// Review detail response
interface ReviewDetail {
  id: string;
  parent_job_run_id: string;
  target_job_type: string;
  reviewer_model: string;
  prompt_version: string;
  created_at: string;
  finished_at: string | null;
  items_attempted: number;
  items_suggested: number;
  input_tokens: number;
  output_tokens: number;
  estimated_cost_usd: number;
  approved_at: string | null;
  applied_at: string | null;
  // Derived
  counts: {
    queued: number;
    processing: number;
    succeeded: number;
    failed: number;
  };
}

// Review item
interface ReviewItem {
  media_type: string;
  tmdb_id: number;
  job_type: string;
  status: string;
  before_json: Record<string, unknown>;
  suggested_json: Record<string, unknown>;
  confidence: { overall: number; review_certainty?: number };
  reason: string;
  has_changes: boolean;
  approved: boolean;
  approved_at: string | null;
  applied: boolean;
  applied_at: string | null;
  title?: string;
  error_code?: string | null;
  error_message?: string | null;
}

interface ReviewItemsResponse {
  items: ReviewItem[];
  next_cursor: { media_type: string; tmdb_id: number } | null;
}

type ItemFilter = 'all' | 'has_changes' | 'approved' | 'applied' | 'not_approved';

function formatJobType(jobType: string): string {
  switch (jobType) {
    case 'ai_facets_v1': return 'AI Facets';
    case 'ai_overlay_genres_v1': return 'AI Overlay Genres';
    case 'ai_microtag_v1': return 'AI Microtag';
    default: return jobType;
  }
}

function formatCost(cost: number): string {
  return `$${cost.toFixed(4)}`;
}

export function AdminReviewDetailPage() {
  // Route is /admin/reviews/:id - use 'id' param name
  const { id: reviewId } = useParams<{ id: string }>();
  const { session } = useAuth();

  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<{ media_type: string; tmdb_id: number } | null>(null);
  // Default to 'all' so queued/processing items show while review is running
  const [filter, setFilter] = useState<ItemFilter>('all');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const fetchReview = useCallback(async () => {
    if (!session?.access_token || !reviewId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiFetch<ReviewDetail>(`/admin/reviews/${reviewId}`, {
        token: session.access_token,
      });
      setReview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch review');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, reviewId]);

  // Map UI filter to backend query params
  // Backend expects: has_changes=true/false, approved=true/false, applied=true/false
  const buildFilterParams = (f: ItemFilter): string => {
    switch (f) {
      case 'has_changes':
        return '&has_changes=true';
      case 'approved':
        return '&approved=true';
      case 'applied':
        return '&applied=true';
      case 'not_approved':
        return '&has_changes=true&approved=false';
      case 'all':
      default:
        return '';
    }
  };

  const fetchItems = useCallback(async (cursor?: { media_type: string; tmdb_id: number }) => {
    if (!session?.access_token || !reviewId) return;

    setItemsLoading(true);

    try {
      let url = `/admin/reviews/${reviewId}/items?limit=50`;
      url += buildFilterParams(filter);
      if (cursor) {
        url += `&cursor_media_type=${cursor.media_type}&cursor_tmdb_id=${cursor.tmdb_id}`;
      }

      const data = await apiFetch<ReviewItemsResponse>(url, {
        token: session.access_token,
      });

      if (cursor) {
        setItems(prev => [...prev, ...data.items]);
      } else {
        setItems(data.items);
      }
      setNextCursor(data.next_cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch items');
    } finally {
      setItemsLoading(false);
    }
  }, [session?.access_token, reviewId, filter]);

  useEffect(() => {
    fetchReview();
  }, [fetchReview]);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    setSelectedItems(new Set());
    fetchItems();
  }, [filter, reviewId, fetchItems]);

  const toggleExpand = (key: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleSelect = (key: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const selectAll = () => {
    const keys = items.filter(i => i.has_changes && !i.approved).map(i => `${i.media_type}:${i.tmdb_id}`);
    setSelectedItems(new Set(keys));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  const handleApproveSelected = async () => {
    if (!session?.access_token || !reviewId || selectedItems.size === 0) return;

    setActionLoading(true);
    setActionMessage(null);

    try {
      const itemsToApprove = Array.from(selectedItems).map(key => {
        const [media_type, tmdb_id] = key.split(':');
        return { media_type, tmdb_id: parseInt(tmdb_id, 10) };
      });

      // apiFetch already JSON.stringifies body - pass object directly
      await apiFetch(`/admin/reviews/${reviewId}/approve`, {
        method: 'POST',
        token: session.access_token,
        body: { items: itemsToApprove },
      });

      setActionMessage(`Approved ${itemsToApprove.length} items`);
      setSelectedItems(new Set());
      fetchReview();
      fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve items');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveAll = async () => {
    if (!session?.access_token || !reviewId) return;

    setActionLoading(true);
    setActionMessage(null);

    try {
      // apiFetch already JSON.stringifies body - pass object directly
      const result = await apiFetch<{ approved_count: number }>(`/admin/reviews/${reviewId}/approve`, {
        method: 'POST',
        token: session.access_token,
        body: { approve_all: true },
      });

      setActionMessage(`Approved ${result.approved_count} items`);
      setSelectedItems(new Set());
      fetchReview();
      fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve all items');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApply = async () => {
    if (!session?.access_token || !reviewId) return;

    setActionLoading(true);
    setActionMessage(null);

    try {
      // apiFetch already JSON.stringifies body - pass object directly
      const result = await apiFetch<{ applied_count: number }>(`/admin/reviews/${reviewId}/apply`, {
        method: 'POST',
        token: session.access_token,
        body: {},
      });

      setActionMessage(`Applied ${result.applied_count} suggestions`);
      fetchReview();
      fetchItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply suggestions');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!review) return <Clock size={16} className="text-slate-400" />;
    if (!review.finished_at) return <Clock size={16} className="text-blue-400 animate-pulse" />;
    if (review.applied_at) return <CheckCircle2 size={16} className="text-green-400" />;
    if (review.approved_at) return <Check size={16} className="text-purple-400" />;
    if (review.items_suggested > 0) return <AlertTriangle size={16} className="text-yellow-400" />;
    return <CheckCircle2 size={16} className="text-slate-400" />;
  };

  const getStatusText = () => {
    if (!review) return 'Loading...';
    if (!review.finished_at) return 'Processing';
    if (review.applied_at) return 'Applied';
    if (review.approved_at) return 'Approved';
    if (review.items_suggested > 0) return 'Changes Suggested';
    return 'No Changes';
  };

  return (
    <AdminLayout
      title="Review Detail"
      description="View and manage AI review results"
    >
      {/* Back link */}
      <Link
        to="/admin/jobs"
        className="flex items-center gap-1 text-sm text-slate-400 hover:text-teal-400 mb-4"
      >
        <ChevronLeft size={16} />
        Back to Jobs
      </Link>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Action message */}
      {actionMessage && (
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg mb-4">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle2 size={16} />
            <span>{actionMessage}</span>
          </div>
        </div>
      )}

      {/* Review Summary */}
      {review && (
        <div className="p-4 bg-[#1A1F26] rounded-lg border border-white/[0.08] mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {getStatusIcon()}
              <h2 className="text-lg font-semibold text-white">{getStatusText()}</h2>
            </div>
            <button
              onClick={() => { fetchReview(); fetchItems(); }}
              disabled={loading || itemsLoading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 bg-[#0F1419] rounded-lg hover:text-white transition-colors"
            >
              <RefreshCw size={14} className={(loading || itemsLoading) ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-400">Parent Job</p>
              <Link
                to={`/admin/jobs/${review.parent_job_run_id}`}
                className="text-teal-400 hover:text-teal-300 flex items-center gap-1"
              >
                {review.parent_job_run_id.slice(0, 8)}...
                <ExternalLink size={12} />
              </Link>
            </div>
            <div>
              <p className="text-slate-400">Job Type</p>
              <p className="text-white">{formatJobType(review.target_job_type)}</p>
            </div>
            <div>
              <p className="text-slate-400">Model</p>
              <p className="text-white">{review.reviewer_model.split('-').slice(0, 2).join('-')}</p>
            </div>
            <div>
              <p className="text-slate-400">Cost</p>
              <p className="text-white">{formatCost(review.estimated_cost_usd)}</p>
            </div>
            <div>
              <p className="text-slate-400">Items</p>
              <p className="text-white">{review.items_attempted}</p>
            </div>
            <div>
              <p className="text-slate-400">Suggested</p>
              <p className="text-yellow-400">{review.items_suggested}</p>
            </div>
            <div>
              <p className="text-slate-400">Started</p>
              <p className="text-white">{new Date(review.created_at).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-slate-400">Finished</p>
              <p className="text-white">{review.finished_at ? new Date(review.finished_at).toLocaleString() : 'Running...'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      {review && review.finished_at && review.items_suggested > 0 && (
        <div className="flex items-center gap-2 mb-4">
          {!review.approved_at && (
            <>
              <button
                onClick={handleApproveSelected}
                disabled={actionLoading || selectedItems.size === 0}
                className="px-4 py-2 text-sm font-medium bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Approve Selected ({selectedItems.size})
              </button>
              <button
                onClick={handleApproveAll}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Approve All
              </button>
            </>
          )}
          {review.approved_at && !review.applied_at && (
            <button
              onClick={handleApply}
              disabled={actionLoading}
              className="px-4 py-2 text-sm font-medium bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Apply All Approved
            </button>
          )}
          {actionLoading && <RefreshCw size={16} className="text-slate-400 animate-spin" />}
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-4">
        {(['all', 'has_changes', 'approved', 'applied', 'not_approved'] as ItemFilter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filter === f
                ? 'bg-teal-500 text-white'
                : 'bg-[#1A1F26] text-slate-300 hover:text-white'
            }`}
          >
            {f === 'all' ? 'All' : f === 'has_changes' ? 'Has Changes' : f === 'approved' ? 'Approved' : f === 'applied' ? 'Applied' : 'Not Approved'}
          </button>
        ))}
        {filter === 'has_changes' && items.some(i => i.has_changes && !i.approved) && (
          <div className="ml-auto flex gap-2">
            <button onClick={selectAll} className="text-xs text-teal-400 hover:text-teal-300">Select All</button>
            <button onClick={deselectAll} className="text-xs text-slate-400 hover:text-slate-300">Deselect All</button>
          </div>
        )}
      </div>

      {/* Items list */}
      <div className="space-y-2">
        {items.map((item) => {
          const key = `${item.media_type}:${item.tmdb_id}`;
          const isExpanded = expandedItems.has(key);
          const isSelected = selectedItems.has(key);
          const canSelect = item.has_changes && !item.approved;

          return (
            <div
              key={key}
              className="p-3 bg-[#1A1F26] rounded-lg border border-white/[0.08]"
            >
              <div className="flex items-center gap-3">
                {canSelect && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(key)}
                    className="w-4 h-4 rounded bg-[#0F1419] border-white/[0.08] text-teal-500 focus:ring-teal-500"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">
                      {item.title || `${item.media_type}/${item.tmdb_id}`}
                    </span>
                    <span className="text-xs text-slate-400">
                      {item.media_type}/{item.tmdb_id}
                    </span>
                    {item.has_changes && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                        Changes
                      </span>
                    )}
                    {item.approved && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                        Approved
                      </span>
                    )}
                    {item.applied && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                        Applied
                      </span>
                    )}
                    {item.status === 'failed' && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        item.error_code === 'INVALID_REVIEW_OUTPUT'
                          ? 'bg-orange-500/20 text-orange-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.error_code === 'INVALID_REVIEW_OUTPUT' ? 'Invalid (OOV)' : 'Failed'}
                      </span>
                    )}
                  </div>
                  {item.reason && (
                    <p className="text-sm text-slate-400 mt-1 truncate">{item.reason}</p>
                  )}
                </div>
                <button
                  onClick={() => toggleExpand(key)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>

              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-white/[0.08] space-y-3">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Before</p>
                    <pre className="text-xs text-slate-300 bg-[#0F1419] p-2 rounded overflow-x-auto max-h-48">
                      {JSON.stringify(item.before_json, null, 2)}
                    </pre>
                  </div>
                  {item.has_changes && item.suggested_json && Object.keys(item.suggested_json).length > 0 && (
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Suggested</p>
                      <pre className="text-xs text-green-300 bg-[#0F1419] p-2 rounded overflow-x-auto max-h-48">
                        {JSON.stringify(item.suggested_json, null, 2)}
                      </pre>
                    </div>
                  )}
                  {(item.error_code || item.error_message) && (
                    <div className={`p-2 rounded ${
                      item.error_code === 'INVALID_REVIEW_OUTPUT'
                        ? 'bg-orange-500/10 border border-orange-500/20'
                        : 'bg-red-500/10 border border-red-500/20'
                    }`}>
                      <p className={`text-xs font-medium mb-1 ${
                        item.error_code === 'INVALID_REVIEW_OUTPUT' ? 'text-orange-400' : 'text-red-400'
                      }`}>
                        {item.error_code === 'INVALID_REVIEW_OUTPUT'
                          ? 'Invalid Review Output (Out-of-Vocabulary Tokens)'
                          : 'Error'}
                      </p>
                      {item.error_code && (
                        <p className="text-xs text-slate-400 mb-1">
                          <span className="font-mono">{item.error_code}</span>
                        </p>
                      )}
                      {item.error_message && (
                        <p className={`text-xs ${
                          item.error_code === 'INVALID_REVIEW_OUTPUT' ? 'text-orange-300' : 'text-red-300'
                        }`}>
                          {item.error_message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {items.length === 0 && !itemsLoading && (
          <p className="text-center text-slate-500 py-8">No items found</p>
        )}

        {itemsLoading && (
          <div className="flex justify-center py-4">
            <RefreshCw size={20} className="text-slate-400 animate-spin" />
          </div>
        )}

        {nextCursor && !itemsLoading && (
          <button
            onClick={() => fetchItems(nextCursor)}
            className="w-full py-2 text-sm text-teal-400 hover:text-teal-300 transition-colors"
          >
            Load More
          </button>
        )}
      </div>
    </AdminLayout>
  );
}
